import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, ListGroup, Badge, FormCheck } from 'react-bootstrap';
import { collection, getDocs } from 'firebase/firestore';
import { obtenerContenidos } from '../servicios/consultas';
import { db } from '../servicios/firebase';

const FormularioUsuario = ({ onSubmit, onCancel, initialData }) => {
  const [nuevoUsuario, setNuevoUsuario] = useState({
    email: '',
    password: '',
    cédula: '',
    nombre: '',
    apellido: '',
    grado: '',
    sección: '',
    rol: 'usuario',
    contenidos: [],
    telefono: '',
    estado: 'activo',
    ...initialData
  });

  const [contenidosDisponibles, setContenidosDisponibles] = useState([]);
  const [selectedContenido, setSelectedContenido] = useState('');
  const [selectedCertEstado, setSelectedCertEstado] = useState('oculto');
  const [dominioEmail] = useState('@ole.edu');
  const [loadingContenidos, setLoadingContenidos] = useState(true);

  // Validación mejorada
  const isFormValid = () => {
    return (
      /^\d{7,10}$/.test(nuevoUsuario.cédula) &&
      nuevoUsuario.nombre.trim() &&
      nuevoUsuario.apellido.trim() &&
      nuevoUsuario.contenidos.length > 0
    );
  };

  // Cargar contenidos disponibles
  useEffect(() => {
    const cargarContenidos = async () => {
      try {
        const contenidos = await obtenerContenidos();
        setContenidosDisponibles(contenidos);
      } catch (error) {
        console.error('Error cargando contenidos:', error);
      }
      setLoadingContenidos(false);
    };
    cargarContenidos();
  }, []);

  // Cargar certificados existentes al editar
  useEffect(() => {
    if (initialData?.id) {
      const cargarCertificados = async () => {
        try {
          const certificadosRef = collection(db, 'usuarios', initialData.id, 'certificados');
          const snapshot = await getDocs(certificadosRef);
          
          setNuevoUsuario(prev => ({
            ...prev,
            contenidos: snapshot.docs.map(doc => ({
              contenidoId: doc.data().contenidoId,
              estado: doc.data().estado
            }))
          }));
        } catch (error) {
          console.error('Error cargando certificados:', error);
        }
      };
      cargarCertificados();
    }
  }, [initialData]);

  // Generar email automático solo en creación
  useEffect(() => {
    if (!initialData?.email && nuevoUsuario.cédula) {
      const cedulaLimpia = nuevoUsuario.cédula.replace(/\D/g, '');
      setNuevoUsuario(prev => ({
        ...prev,
        email: `${cedulaLimpia}${dominioEmail}`,
        password: cedulaLimpia
      }));
    }
  }, [nuevoUsuario.cédula, dominioEmail, initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNuevoUsuario(prev => ({ 
      ...prev, 
      [name]: name === 'cédula' ? value.replace(/\D/g, '') : value 
    }));
  };

  // Evitar duplicados al agregar contenido
  const handleAddContenido = () => {
    if (selectedContenido && 
        !nuevoUsuario.contenidos.some(c => c.contenidoId === selectedContenido)) {
      
      setNuevoUsuario(prev => ({
        ...prev,
        contenidos: [
          ...prev.contenidos,
          { 
            contenidoId: selectedContenido, 
            estado: selectedCertEstado 
          }
        ]
      }));
      setSelectedContenido('');
      setSelectedCertEstado('oculto');
    }
  };

  const handleCertificadoEstadoChange = (index, newEstado) => {
    const nuevosContenidos = [...nuevoUsuario.contenidos];
    nuevosContenidos[index].estado = newEstado;
    setNuevoUsuario(prev => ({ ...prev, contenidos: nuevosContenidos }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      alert('Complete cédula (7-10 dígitos), nombre, apellido y agregue al menos un contenido');
      return;
    }
    onSubmit(nuevoUsuario);
  };

  const getNombreCurso = (contenidoId) => {
    const contenido = contenidosDisponibles.find(c => c.id === contenidoId);
    return contenido ? `${contenido.curso} (${contenido.código})` : contenidoId;
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Sección Estado Usuario */}
      <Row className="mb-4">
        <Col md={12}>
          <FormCheck
            type="switch"
            id="estado-usuario"
            label="Usuario Activo"
            checked={nuevoUsuario.estado === 'activo'}
            onChange={(e) => 
              setNuevoUsuario(prev => ({
                ...prev,
                estado: e.target.checked ? 'activo' : 'inactivo'
              }))
            }
          />
        </Col>
      </Row>

      {/* Campos principales */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Cédula *</Form.Label>
            <Form.Control
              type="text"
              name="cédula"
              value={nuevoUsuario.cédula}
              onChange={handleInputChange}
              readOnly={!!initialData}
              className={initialData ? 'bg-light' : ''}
              required
              pattern="\d{7,10}"
              title="7-10 dígitos numéricos"
              placeholder="Ej: 18500857"
            />
          </Form.Group>
        </Col>
        
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Email *</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={nuevoUsuario.email}
              readOnly
              className="bg-light"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Nombre *</Form.Label>
            <Form.Control
              type="text"
              name="nombre"
              value={nuevoUsuario.nombre}
              onChange={handleInputChange}
              required
              placeholder="Ej: Lisbeth"
            />
          </Form.Group>
        </Col>
        
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Apellido *</Form.Label>
            <Form.Control
              type="text"
              name="apellido"
              value={nuevoUsuario.apellido}
              onChange={handleInputChange}
              required
              placeholder="Ej: Badillo"
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Grado</Form.Label>
            <Form.Control
              type="number"
              name="grado"
              value={nuevoUsuario.grado}
              min="1"
              max="12"
              onChange={handleInputChange}
              placeholder="Ej: 7"
            />
          </Form.Group>
        </Col>
        
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Sección</Form.Label>
            <Form.Control
              type="text"
              name="sección"
              value={nuevoUsuario.sección}
              onChange={handleInputChange}
              placeholder="Ej: A"
            />
          </Form.Group>
        </Col>
        
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Teléfono</Form.Label>
            <Form.Control
              type="text"
              name="telefono"
              value={nuevoUsuario.telefono}
              onChange={handleInputChange}
              placeholder="Ej: +58 414 1234567"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Sección Contenidos y Certificados */}
      <Form.Group className="mb-4">
        <Form.Label>Contenidos Asociados *</Form.Label>
        <div className="d-flex gap-2 mb-3">
          <Form.Select
            value={selectedContenido}
            onChange={(e) => setSelectedContenido(e.target.value)}
            disabled={loadingContenidos}
            style={{ flex: 2 }}
          >
            <option value="">{loadingContenidos ? 'Cargando...' : 'Seleccionar curso'}</option>
            {contenidosDisponibles.map(contenido => (
              <option 
                key={contenido.id} 
                value={contenido.id}
                disabled={nuevoUsuario.contenidos.some(c => c.contenidoId === contenido.id)}
              >
                {contenido.curso} ({contenido.código})
              </option>
            ))}
          </Form.Select>

          <Form.Select
            value={selectedCertEstado}
            onChange={(e) => setSelectedCertEstado(e.target.value)}
            style={{ flex: 1 }}
          >
            <option value="oculto">Oculto</option>
            <option value="activo">Activo</option>
          </Form.Select>

          <Button 
            variant="outline-primary" 
            onClick={handleAddContenido}
            disabled={!selectedContenido || loadingContenidos}
          >
            Agregar
          </Button>
        </div>

        <ListGroup>
          {nuevoUsuario.contenidos.map((item, index) => (
            <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
              <div>
                {getNombreCurso(item.contenidoId)}
                <Badge bg="secondary" className="ms-2">
                  {item.estado}
                </Badge>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Form.Select
                  value={item.estado}
                  onChange={(e) => handleCertificadoEstadoChange(index, e.target.value)}
                  size="sm"
                  style={{ width: '120px' }}
                >
                  <option value="oculto">Oculto</option>
                  <option value="activo">Activo</option>
                </Form.Select>
                <Badge 
                  bg="danger" 
                  role="button" 
                  onClick={() => setNuevoUsuario(prev => ({
                    ...prev,
                    contenidos: prev.contenidos.filter((_, i) => i !== index)
                  }))}
                >
                  ✕
                </Badge>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Form.Group>

      {/* Campos ocultos para datos automáticos */}
      <input type="hidden" name="password" value={nuevoUsuario.password} />
      <input type="hidden" name="rol" value="usuario" />

      <div className="modal-footer">
        <Button variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={!isFormValid()}>
          {initialData ? 'Actualizar Usuario' : 'Registrar Usuario'}
        </Button>
      </div>
    </Form>
  );
};

FormularioUsuario.defaultProps = {
  initialData: {
    email: '',
    password: '',
    cédula: '',
    nombre: '',
    apellido: '',
    grado: '',
    sección: '',
    rol: 'usuario',
    contenidos: [],
    telefono: '',
    estado: 'activo'
  }
};

export default FormularioUsuario;